import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Facility } from "@/hooks/useFacilities";
import { EditFacilityDialog } from "./EditFacilityDialog";
import { FacilityVideoDialog } from "./FacilityVideoDialog";
import { Trash2, Play } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface FacilityCardProps {
  facility: Facility;
  onFacilityUpdated: () => void;
}

export const FacilityCard = ({ facility, onFacilityUpdated }: FacilityCardProps) => {
  const { toast } = useToast();
  const [videoOpen, setVideoOpen] = useState(false);

  const handleDelete = async () => {
    try {
      const { error } = await supabase
        .from('facilities')
        .delete()
        .eq('id', facility.id);

      if (error) {
        throw error;
      }

      toast({
        title: "Success",
        description: "Facility deleted successfully!",
      });

      onFacilityUpdated();
    } catch (error: any) {
      console.error("Error deleting facility:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete facility",
        variant: "destructive",
      });
    }
  };

  const formatFacilityType = (type: string) => {
    return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <>
      <Card className="overflow-hidden">
        {facility.image_url && (
          <div 
            className={`aspect-video relative overflow-hidden ${facility.video_url ? 'cursor-pointer group' : ''}`}
            onClick={() => facility.video_url && setVideoOpen(true)}
          >
            <img
              src={facility.image_url}
              alt={facility.name}
              className="w-full h-full object-cover"
            />
            {facility.video_url && (
              <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-100 group-hover:bg-black/50 transition-colors">
                <div className="bg-white/90 rounded-full p-3 shadow-lg">
                  <Play className="h-8 w-8 text-primary fill-primary" />
                </div>
              </div>
            )}
          </div>
        )}
        {!facility.image_url && facility.video_url && (
          <div 
            className="aspect-video relative overflow-hidden bg-muted cursor-pointer group"
            onClick={() => setVideoOpen(true)}
          >
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="bg-primary rounded-full p-3 shadow-lg group-hover:scale-110 transition-transform">
                <Play className="h-8 w-8 text-primary-foreground fill-primary-foreground" />
              </div>
            </div>
          </div>
        )}
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <CardTitle className="text-lg">{facility.name}</CardTitle>
            <Badge variant="secondary" className="mt-2">
              {formatFacilityType(facility.facility_type)}
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {facility.description && (
          <p className="text-sm text-gray-600 mb-4 line-clamp-3">
            {facility.description}
          </p>
        )}
        <div className="flex space-x-2">
          <EditFacilityDialog
            facility={facility}
            onFacilityUpdated={onFacilityUpdated}
          />
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm">
                <Trash2 className="h-4 w-4 mr-1" />
                Delete
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Facility</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete "{facility.name}"? This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleDelete}>
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </CardContent>
    </Card>
    
    {facility.video_url && (
      <FacilityVideoDialog
        videoUrl={facility.video_url}
        facilityName={facility.name}
        open={videoOpen}
        onOpenChange={setVideoOpen}
      />
    )}
  </>
  );
};
